import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from './supabase/supabaseClient.js';
import { useTranslation } from 'react-i18next';
import { FaPhone, FaLine, FaArrowLeft } from 'react-icons/fa';
import "./products.css";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language ? i18n.language.split('-')[0] : 'th';

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // 🟢 State สำหรับจัดการรูปภาพที่กำลังแสดงผล
  const [mainImage, setMainImage] = useState('');

  useEffect(() => {
    const fetchProductDetails = async () => {
      try {
        setLoading(true);
        const { data: mainProduct, error: mainError } = await supabase
          .from('products')
          .select('*')
          .eq('id', id)
          .single();
        
        if (mainError) throw mainError;
        setProduct(mainProduct);
        
        // 🟢 ตั้งค่ารูปเริ่มต้นเป็นรูปหลัก
        setMainImage(mainProduct.image);

        if (mainProduct && mainProduct.category) {
          const { data: relatedData, error: relatedError } = await supabase
            .from('products')
            .select('*')
            .eq('category', mainProduct.category)
            .neq('id', id)
            .limit(4);
            
          if (!relatedError && relatedData) {
            setRelatedProducts(relatedData);
          }
        }
      } catch (err) {
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) return <div className="loading-container" style={{ minHeight: '100vh' }}><div className="spinner"></div></div>;
  if (!product) return <div style={{ padding: '150px', textAlign: 'center' }}><h2>ไม่พบสินค้านี้</h2><button onClick={() => navigate('/products')} className="reset-btn">กลับหน้าสินค้า</button></div>;

  // 🟢 รวมรูปภาพทั้งหมดที่มีข้อมูลเพื่อทำ Gallery
  const allImages = [product.image, product.image2, product.image3, product.image4].filter(img => img);

  return (
    <section className="product-detail-page">
      <div className="product-detail-wrapper">
        
        <button onClick={() => navigate(-1)} className="back-btn">
          <FaArrowLeft /> ย้อนกลับ
        </button>

        <div className="detail-container clean-layout">
          
          {/* 🟢 ฝั่งซ้าย: รูปภาพและ Gallery */}
          <div className="detail-image-side">
            <div className="image-box-clean">
              <img src={mainImage || 'https://via.placeholder.com/600'} alt={product.name} />
            </div>

            {/* ส่วนรูป Thumbnail ด้านล่าง (Gallery) */}
            {allImages.length > 1 && (
              <div className="image-gallery-thumbnails" style={{ display: 'flex', gap: '10px', marginTop: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                {allImages.map((img, idx) => (
                  <img 
                    key={idx}
                    src={img} 
                    alt={`thumb-${idx}`}
                    onClick={() => setMainImage(img)}
                    style={{ 
                      width: '70px', height: '70px', objectFit: 'cover', borderRadius: '8px',
                      cursor: 'pointer', border: mainImage === img ? '2px solid #0ea5e9' : '1px solid #e2e8f0',
                      opacity: mainImage === img ? 1 : 0.6,
                      transition: 'all 0.2s ease'
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* ฝั่งขวา: ข้อมูลสินค้า */}
          <div className="detail-info-side">
             <div className="tags-row">
                <span className="tag-category">{product.category}</span>
                <span className="tag-status">AVAILABLE</span>
             </div>

             <h1 className="product-title-clean">
               {product[`name_${currentLang}`] || product.name}
             </h1>
             
             <div className="title-divider-yellow"></div>
             
             {/* กล่องรายละเอียดสินค้า (Specs Box) */}
             <div className="specs-box-clean">
               <div className="specs-header">
                 <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16" style={{ marginRight: '8px' }}>
                    <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"/>
                 </svg>
                 รายละเอียดสินค้า
               </div>
               <div className="spec-row">
                 <span className="spec-label">ยี่ห้อ (Brand)</span>
                 <span className="spec-value">{product.brand || '-'}</span>
               </div>
               <div className="spec-row">
                 <span className="spec-label">รุ่น (Model)</span>
                 <span className="spec-value">{product.model || '-'}</span>
               </div>
               <div className="spec-row">
                 <span className="spec-label">ราคา (Price)</span>
                 <span className="spec-value price-text">
                   {product.price ? `${Number(product.price).toLocaleString()} บาท` : 'ติดต่อสอบถาม'}
                 </span>
               </div>
             </div>

             <div className="description-section-clean">
                <h3>{t('productDetailsTitle', 'รายละเอียดเพิ่มเติม (DESCRIPTION)')}</h3>
                <p style={{ whiteSpace: 'pre-line' }}>
                  {product[`description_${currentLang}`] || product.description}
                </p>
             </div>
             
             <div className="modal-actions-premium" style={{ marginTop: '40px' }}>
                <div className="premium-btn-group">
                   <a href="https://line.me/ti/p/~@730aicdq" className="premium-btn line-gold" target="_blank" rel="noreferrer"><FaLine /> ติดต่อผ่าน LINE</a>
                   <a href="tel:0906187997" className="premium-btn tel-dark"><FaPhone /> โทรสอบถาม</a>
                </div>
             </div>
          </div>
        </div>

        {/* ส่วนแสดงสินค้าที่เกี่ยวข้อง */}
        {relatedProducts.length > 0 && (
          <div className="related-products-section">
            <h2 className="related-title">สินค้าที่เกี่ยวข้องในหมวด <span>{product.category}</span></h2>
            <div className="products-grid">
              {relatedProducts.map((relProduct) => (
                <div key={relProduct.id} className="product-card" onClick={() => navigate(`/product/${relProduct.id}`)}>
                  <div className="product-image-wrapper">
                    <img src={relProduct.image || 'https://via.placeholder.com/300'} alt={relProduct.name} className="product-image" />
                  </div>
                  <div className="product-content">
                    <span className="product-category-tag">{relProduct.category}</span>
                    <h3 style={{ fontSize: '1.1rem' }}>{relProduct[`name_${currentLang}`] || relProduct.name}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </section>
  );
}